import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { settingsApi } from '../../services/api';
import { Modal, Button, Form, InputGroup, Spinner } from 'react-bootstrap';

const Settings = () => {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Key rotation state
  const [showRotateKeyModal, setShowRotateKeyModal] = useState(false);
  const [newEncryptionKey, setNewEncryptionKey] = useState('');
  const [showNewKey, setShowNewKey] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [rotateError, setRotateError] = useState(null);
  const [showRestartModal, setShowRestartModal] = useState(false);
  const [showNewKeyInModal, setShowNewKeyInModal] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Check if user has permission to access settings
    if (!hasPermission('settings', 'view')) {
      navigate('/unauthorized');
      return;
    }

    // Fetch settings
    fetchSettings();
  }, [hasPermission, navigate]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await settingsApi.getSettings();
      setSettings(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load settings. Please try again.');
      console.error('Error fetching settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        value: value
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      setSuccess(false);
      
      // Convert settings to the format expected by the API
      const settingsToSave = {};
      Object.keys(settings).forEach(key => {
        settingsToSave[key] = settings[key].value;
      });
      
      await settingsApi.updateSettings(settingsToSave);
      
      // If APP_NAME was updated, update the translations
      if (settingsToSave.APP_NAME) {
        // Update the translation resources for current language
        const resources = i18n.getResourceBundle(i18n.language, 'common');
        if (resources && resources.app) {
          resources.app.name = settingsToSave.APP_NAME;
          i18n.addResourceBundle(i18n.language, 'common', resources, true, true);
          
          // Also update other languages
          const otherLang = i18n.language === 'en' ? 'de' : 'en';
          const otherResources = i18n.getResourceBundle(otherLang, 'common');
          if (otherResources && otherResources.app) {
            otherResources.app.name = settingsToSave.APP_NAME;
            i18n.addResourceBundle(otherLang, 'common', otherResources, true, true);
          }
        }
      }
      
      setSuccess(true);
      setError(null);
    } catch (err) {
      setError('Failed to save settings. Please try again.');
      console.error('Error saving settings:', err);
      setSuccess(false);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center my-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Group settings by category
  const groupedSettings = {
    security: ['ENCRYPTION_KEY'],
    email: ['SMTP_SERVER', 'SMTP_PORT', 'SMTP_USERNAME', 'SMTP_PASSWORD', 'EMAIL_FROM', 'EMAIL_ENABLED'],
    telegram: ['TELEGRAM_BOT_TOKEN', 'TELEGRAM_ENABLED'],
    reminders: ['REMINDER_CHECK_INTERVAL']
  };

  return (
    <div className="settings-page">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>
          <i className="fas fa-cogs me-2"></i>
          Application Settings
        </h1>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success" role="alert">
          <i className="fas fa-check-circle me-2"></i>
          Settings saved successfully.
        </div>
      )}

      <div className="row">
        <div className="col-12">
          <div className="alert alert-info">
            <i className="fas fa-info-circle me-2"></i>
            Settings can be defined in the environment or stored in the application. Environment values always take precedence.
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} autoComplete="off">
        {Object.keys(settings).length === 0 ? (
          <div className="card shadow-sm mb-4">
            <div className="card-body">
              <p className="text-muted">No settings available.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="row">
              {/* Left Column */}
              <div className="col-md-6">
                {/* Security Settings */}
                <div className="card shadow-sm mb-4">
                  <div className="card-header bg-primary text-white">
                    <h5 className="mb-0">
                      <i className="fas fa-shield-alt me-2"></i>
                      Security Settings
                    </h5>
                  </div>
                  <div className="card-body">
                    {/* Application Name */}
                    {settings["APP_NAME"] && (
                      <div className="mb-3">
                        <label htmlFor="APP_NAME" className="form-label fw-bold">
                          {settings["APP_NAME"].display_name}
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="APP_NAME"
                          value={settings["APP_NAME"].value || ''}
                          onChange={(e) => handleChange("APP_NAME", e.target.value)}
                          disabled={saving}
                        />
                        {settings["APP_NAME"].from_env && (
                          <div className="form-text text-warning">
                            <i className="fas fa-exclamation-triangle me-1"></i>
                            This setting is defined in the environment and will override any value set here.
                          </div>
                        )}
                        {settings["APP_NAME"].env_value && (
                          <div className="form-text text-muted">
                            <i className="fas fa-server me-1"></i>
                            Environment value: <code>{settings["APP_NAME"].env_value}</code>
                          </div>
                        )}
                        {settings["APP_NAME"].description && (
                          <div className="form-text">
                            {settings["APP_NAME"].description}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {groupedSettings.security.map(key => (
                      settings[key] && (
                        <div className="mb-3" key={key}>
                          <label htmlFor={key} className="form-label fw-bold">
                            {settings[key].display_name}
                          </label>
                          <div className="input-group">
                            <input
                              type={settings[key].type === 'password' ? 'password' : 'text'}
                              className={`form-control ${key === 'ENCRYPTION_KEY' ? 'bg-light' : ''}`}
                              id={key}
                              value={settings[key].value || ''}
                              onChange={(e) => key === 'ENCRYPTION_KEY' ? null : handleChange(key, e.target.value)}
                              disabled={key === 'ENCRYPTION_KEY' ? true : saving}
                              autoComplete="new-password"
                              readOnly={key === 'ENCRYPTION_KEY'}
                            />
                            {settings[key].type === 'password' && (
                              <button 
                                className="btn btn-outline-secondary" 
                                type="button"
                                onClick={() => {
                                  const input = document.getElementById(key);
                                  input.type = input.type === 'password' ? 'text' : 'password';
                                }}
                              >
                                <i className="fas fa-eye"></i>
                              </button>
                            )}
                          </div>
                          {key === 'ENCRYPTION_KEY' && (
                            <div className="form-text text-info">
                              <i className="fas fa-lock me-1"></i>
                              This is the actual encryption key currently in use. It is read-only and cannot be changed through the UI.
                            </div>
                          )}
                          {settings[key].from_env && (
                            <div className="form-text text-warning">
                              <i className="fas fa-exclamation-triangle me-1"></i>
                              This setting is defined in the environment and will override any value set here.
                            </div>
                          )}
                          {settings[key].env_value && (
                            <div className="form-text text-muted">
                              <i className="fas fa-server me-1"></i>
                              Environment value: <code>{settings[key].type === 'password' ? '********' : settings[key].env_value}</code>
                            </div>
                          )}
                          {settings[key].description && (
                            <div className="form-text">
                              {settings[key].description}
                            </div>
                          )}
                          
                          {/* Add Rotate Key button for encryption key */}
                          {key === 'ENCRYPTION_KEY' && (
                            <div className="mt-3">
                              <button 
                                type="button" 
                                className="btn btn-warning" 
                                onClick={() => {
                                  // Generate a random key and open the modal
                                  settingsApi.generateKey()
                                    .then(response => {
                                      setNewEncryptionKey(response.data.key);
                                      setShowRotateKeyModal(true);
                                      setRotateError(null);
                                    })
                                    .catch(err => {
                                      console.error('Error generating key:', err);
                                    });
                                }}
                              >
                                <i className="fas fa-sync-alt me-2"></i>
                                Rotate Encryption Key
                              </button>
                            </div>
                          )}
                        </div>
                      )
                    ))}
                  </div>
                </div>

                {/* Telegram Settings */}
                <div className="card shadow-sm mb-4">
                  <div className="card-header bg-success text-white">
                    <h5 className="mb-0">
                      <i className="fab fa-telegram me-2"></i>
                      Telegram Settings
                    </h5>
                  </div>
                  <div className="card-body">
                    {groupedSettings.telegram.map(key => (
                      settings[key] && (
                        <div className="mb-3" key={key}>
                          <label htmlFor={key} className="form-label fw-bold">
                            {settings[key].display_name}
                          </label>
                          <div className="input-group">
                            <input
                              type={settings[key].type === 'password' ? 'password' : 'text'}
                              className="form-control"
                              id={key}
                              value={settings[key].value || ''}
                              onChange={(e) => handleChange(key, e.target.value)}
                              disabled={saving}
                              autoComplete="new-password"
                            />
                            {settings[key].type === 'password' && (
                              <button 
                                className="btn btn-outline-secondary" 
                                type="button"
                                onClick={() => {
                                  const input = document.getElementById(key);
                                  input.type = input.type === 'password' ? 'text' : 'password';
                                }}
                              >
                                <i className="fas fa-eye"></i>
                              </button>
                            )}
                          </div>
                          {settings[key].from_env && (
                            <div className="form-text text-warning">
                              <i className="fas fa-exclamation-triangle me-1"></i>
                              This setting is defined in the environment and will override any value set here.
                            </div>
                          )}
                          {settings[key].env_value && (
                            <div className="form-text text-muted">
                              <i className="fas fa-server me-1"></i>
                              Environment value: <code>{settings[key].type === 'password' ? '********' : settings[key].env_value}</code>
                            </div>
                          )}
                          {settings[key].description && (
                            <div className="form-text">
                              {settings[key].description}
                            </div>
                          )}
                        </div>
                      )
                    ))}
                  </div>
                </div>

                {/* Reminder Settings */}
                <div className="card shadow-sm mb-4">
                  <div className="card-header bg-warning text-dark">
                    <h5 className="mb-0">
                      <i className="fas fa-bell me-2"></i>
                      Reminder Settings
                    </h5>
                  </div>
                  <div className="card-body">
                    {groupedSettings.reminders.map(key => (
                      settings[key] && (
                        <div className="mb-3" key={key}>
                          <label htmlFor={key} className="form-label fw-bold">
                            {settings[key].display_name}
                          </label>
                          <input
                            type={settings[key].type === 'password' ? 'password' : 'text'}
                            className="form-control"
                            id={key}
                            value={settings[key].value || ''}
                            onChange={(e) => handleChange(key, e.target.value)}
                            disabled={saving}
                            autoComplete="new-password"
                          />
                          {settings[key].from_env && (
                            <div className="form-text text-warning">
                              <i className="fas fa-exclamation-triangle me-1"></i>
                              This setting is defined in the environment and will override any value set here.
                            </div>
                          )}
                          {settings[key].env_value && (
                            <div className="form-text text-muted">
                              <i className="fas fa-server me-1"></i>
                              Environment value: <code>{settings[key].type === 'password' ? '********' : settings[key].env_value}</code>
                            </div>
                          )}
                          {settings[key].description && (
                            <div className="form-text">
                              {settings[key].description}
                            </div>
                          )}
                        </div>
                      )
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="col-md-6">
                {/* Email Settings */}
                <div className="card shadow-sm mb-4">
                  <div className="card-header bg-info text-white">
                    <h5 className="mb-0">
                      <i className="fas fa-envelope me-2"></i>
                      Email Settings
                    </h5>
                  </div>
                  <div className="card-body">
                    {groupedSettings.email.map(key => (
                      settings[key] && (
                        <div className="mb-3" key={key}>
                          <label htmlFor={key} className="form-label fw-bold">
                            {settings[key].display_name}
                          </label>
                          <div className="input-group">
                            <input
                              type={settings[key].type === 'password' ? 'password' : 'text'}
                              className="form-control"
                              id={key}
                              value={settings[key].value || ''}
                              onChange={(e) => handleChange(key, e.target.value)}
                              disabled={saving}
                              autoComplete="new-password"
                            />
                            {settings[key].type === 'password' && (
                              <button 
                                className="btn btn-outline-secondary" 
                                type="button"
                                onClick={() => {
                                  const input = document.getElementById(key);
                                  input.type = input.type === 'password' ? 'text' : 'password';
                                }}
                              >
                                <i className="fas fa-eye"></i>
                              </button>
                            )}
                          </div>
                          {settings[key].from_env && (
                            <div className="form-text text-warning">
                              <i className="fas fa-exclamation-triangle me-1"></i>
                              This setting is defined in the environment and will override any value set here.
                            </div>
                          )}
                          {settings[key].env_value && (
                            <div className="form-text text-muted">
                              <i className="fas fa-server me-1"></i>
                              Environment value: <code>{settings[key].type === 'password' ? '********' : settings[key].env_value}</code>
                            </div>
                          )}
                          {settings[key].description && (
                            <div className="form-text">
                              {settings[key].description}
                            </div>
                          )}
                        </div>
                      )
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-end mt-4 mb-5">
              <button
                type="submit"
                className="btn btn-primary btn-lg"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save me-2"></i>
                    Save Settings
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </form>

      {/* Key Rotation Modal */}
      <Modal show={showRotateKeyModal} onHide={() => setShowRotateKeyModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Rotate Encryption Key</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {rotateError && (
            <div className="alert alert-danger">
              <i className="fas fa-exclamation-circle me-2"></i>
              {rotateError}
            </div>
          )}
          
          <div className="alert alert-warning">
            <i className="fas fa-exclamation-triangle me-2"></i>
            <strong>Warning:</strong> Rotating the encryption key will re-encrypt all data. This process cannot be interrupted once started.
          </div>
          
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>New Encryption Key</Form.Label>
              <InputGroup>
                <Form.Control 
                  type={showNewKey ? "text" : "password"} 
                  value={newEncryptionKey} 
                  onChange={(e) => setNewEncryptionKey(e.target.value)}
                  placeholder="Enter new encryption key"
                  autoComplete="new-password"
                />
                <Button 
                  variant="outline-secondary"
                  onClick={() => setShowNewKey(!showNewKey)}
                >
                  <i className={`fas fa-${showNewKey ? 'eye-slash' : 'eye'}`}></i>
                </Button>
              </InputGroup>
              <Form.Text className="text-muted">
                This key will be used to encrypt all data. Keep it secure.
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRotateKeyModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="warning" 
            onClick={async () => {
              try {
                setRotating(true);
                const response = await settingsApi.rotateEncryptionKey({ new_key: newEncryptionKey });
                
                if (response.data.restart_required) {
                  // Show restart instructions modal
                  setShowRotateKeyModal(false);
                  setShowRestartModal(true);
                }
              } catch (error) {
                setRotateError(error.response?.data?.detail || "Failed to rotate key");
              } finally {
                setRotating(false);
              }
            }}
            disabled={rotating || !newEncryptionKey}
          >
            {rotating ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Rotating...
              </>
            ) : (
              <>
                <i className="fas fa-sync-alt me-2"></i>
                Rotate Key
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Restart Instructions Modal */}
      <Modal show={showRestartModal} backdrop="static" keyboard={false}>
        <Modal.Header>
          <Modal.Title>Encryption Key Rotated Successfully</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="alert alert-success">
            <i className="fas fa-check-circle me-2"></i>
            <strong>Success!</strong> All data has been re-encrypted with the new key.
          </div>
          
          <div className="alert alert-warning">
            <i className="fas fa-exclamation-triangle me-2"></i>
            <strong>Important:</strong> You must update your environment variable before restarting.
          </div>
          
          <div className="mb-3">
            <label className="form-label fw-bold">Your New Encryption Key:</label>
            <div className="input-group">
              <input 
                type={showNewKeyInModal ? "text" : "password"} 
                className="form-control bg-light" 
                value={newEncryptionKey} 
                readOnly 
              />
              <button 
                className="btn btn-outline-secondary" 
                type="button"
                onClick={() => setShowNewKeyInModal(!showNewKeyInModal)}
              >
                <i className={`fas fa-${showNewKeyInModal ? 'eye-slash' : 'eye'}`}></i>
              </button>
              <button 
                className="btn btn-outline-secondary" 
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(newEncryptionKey);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
              >
                <i className={`fas fa-${copied ? 'check' : 'copy'}`}></i>
              </button>
            </div>
            <small className="text-muted">Copy this key to use in your environment variable</small>
          </div>
          
          <h5>Restart Instructions:</h5>
          <ol>
            <li>Update your environment variable with the new key:
              <pre className="bg-light p-2 mt-1 mb-2">
                <code>ENCRYPTION_KEY={newEncryptionKey}</code>
              </pre>
            </li>
            <li>Restart the Docker container:
              <pre className="bg-light p-2 mt-1 mb-2">
                <code>docker compose restart</code>
              </pre>
              Or if you're running without Docker:
              <pre className="bg-light p-2 mt-1">
                <code>Ctrl+C to stop the server, then restart it</code>
              </pre>
            </li>
            <li>After restart, the application will use the new encryption key</li>
          </ol>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => window.location.href = "/login"}>
            <i className="fas fa-sign-out-alt me-2"></i>
            Logout
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Settings;
